/**
 * Article Product Injection
 * Injects product card into article content at strategic points
 */

class ArticleProductInjection {
  constructor() {
    this.articleContent = document.querySelector('.article-content');
    if (!this.articleContent) return;
    
    this.productData = this.getProductData();
    if (!this.productData) return;
    
    this.init();
  }

  getProductData() {
    const contentEl = this.articleContent;
    const productJson = contentEl.getAttribute('data-product');
    
    if (!productJson) return null;
    
    try {
      return JSON.parse(productJson);
    } catch (e) {
      console.warn('[Article product injection] Failed to parse product data', e);
      return null;
    }
  }

  init() {
    const insertionPoint = this.findInsertionPoint();
    if (!insertionPoint) {
      console.warn('[Article product injection] No suitable insertion point found in article content');
      return;
    }
    
    const productCard = this.createProductCard();
    insertionPoint.insertAdjacentElement('beforebegin', productCard);
  }

  findInsertionPoint() {
    // Get all direct block-level elements from article content
    const allElements = Array.from(this.articleContent.children);
    
    if (allElements.length === 0) {
      console.warn('[Article product injection] No elements found in article content');
      return null;
    }

    // Priority 1: Insert before the second or third H2 (deeper in content)
    const h2Elements = allElements.filter(el => el.tagName === 'H2');
    if (h2Elements.length >= 3) {
      // Use 3rd H2 if available
      return h2Elements[2];
    } else if (h2Elements.length >= 2) {
      // Use 2nd H2
      return h2Elements[1];
    }
    
    // Priority 2: Find paragraphs and insert after 5th-6th paragraph (further down)
    const paragraphs = allElements.filter(el => el.tagName === 'P');
    if (paragraphs.length >= 7) {
      // Insert after the 6th paragraph
      const targetP = paragraphs[6];
      const targetIndex = allElements.indexOf(targetP);
      return allElements[targetIndex + 1] || null;
    } else if (paragraphs.length >= 5) {
      // Insert after the 4th paragraph
      const targetP = paragraphs[4];
      const targetIndex = allElements.indexOf(targetP);
      return allElements[targetIndex + 1] || null;
    }

    // Fallback: Insert at 60% down the content (past midpoint)
    const insertPoint = Math.floor(allElements.length * 0.6);
    if (insertPoint > 0 && insertPoint < allElements.length) {
      return allElements[insertPoint];
    }

    return null;
  }

  createProductCard() {
    const product = this.productData;
    const container = document.createElement('div');
    container.className = 'article-product-injection';
    
    // Get first available variant price
    const variant = product.variants?.[0];
    const price = variant ? this.formatMoney(variant.price) : '';
    
    container.innerHTML = `
      <div class="article-product-card">
        <div class="article-product-badge">Featured Product</div>
        <div class="article-product-content">
          <div class="article-product-image">
            <img src="${this.getProductImage(product)}" alt="${this.escapeHtml(product.title)}" loading="lazy">
          </div>
          <div class="article-product-info">
            <h3 class="article-product-title">${this.escapeHtml(product.title)}</h3>
            <p class="article-product-description">
              ${this.truncateText(this.stripHtml(product.description), 250)}
              <a href="${product.url || '/products/' + product.handle}" class="article-product-learn-more">Learn More</a>
            </p>
            <div class="article-product-footer">
              <span class="article-product-price">Starts at ${price}</span>
              <a href="${product.url || '/products/' + product.handle}" class="btn btn-primary btn-sm text-uppercase">Shop Now</a>
            </div>
          </div>
        </div>
      </div>
    `;
    
    return container;
  }

  getProductImage(product) {
    if (product.featured_image) {
      // If it's already a URL
      if (typeof product.featured_image === 'string') {
        return product.featured_image;
      }
      // If it's an object with src
      if (product.featured_image.src) {
        return product.featured_image.src;
      }
    }
    
    // Fallback to first media/image
    if (product.media && product.media.length > 0) {
      return product.media[0].src || product.media[0].preview_image?.src;
    }
    
    if (product.images && product.images.length > 0) {
      return product.images[0];
    }
    
    return '';
  }

  formatMoney(cents) {
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }

  stripHtml(html) {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }

  truncateText(text, length) {
    if (text.length <= length) return text;
    return text.substring(0, length).trim() + '...';
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// Fix accessibility issues in article content
function fixArticleAccessibility() {
  const articleContent = document.querySelector('.article-content');
  if (!articleContent) return;
  
  // Fix heading order - change h4 to h3 in article content
  const h4Elements = articleContent.querySelectorAll('h4[data-end]');
  h4Elements.forEach(h4 => {
    const h3 = document.createElement('h3');
    h3.className = h4.className;
    h3.innerHTML = h4.innerHTML;
    // Copy all attributes
    Array.from(h4.attributes).forEach(attr => {
      h3.setAttribute(attr.name, attr.value);
    });
    h4.parentNode?.replaceChild(h3, h4);
  });
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ArticleProductInjection();
    fixArticleAccessibility();
  });
} else {
  new ArticleProductInjection();
  fixArticleAccessibility();
}

